import { useEffect, useState } from "react";
import { api, inr, num, fmtDate, formatApiError, API_URL } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { StatusPill } from "./Quotations";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BuyingTrendsTab } from "@/pages/SizeIntelligence";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, Legend, PieChart, Pie, Cell } from "recharts";
import { Download, Repeat, TrendingUp } from "lucide-react";
import { toast } from "sonner";

function Kpi({ label, value, sub, accent }) {
  return (
    <div className="border border-slate-200 bg-white rounded-md p-4" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">{label}</div>
      <div className={`mt-2 font-display text-2xl font-bold tabular-nums ${accent || "text-slate-900"}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function OverviewTab() {
  const [sales, setSales] = useState(null);
  const [outstanding, setOutstanding] = useState([]);
  useEffect(() => {
    api.get("/reports/sales").then((r) => setSales(r.data));
    api.get("/reports/outstanding").then((r) => setOutstanding(r.data));
  }, []);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <Kpi label="Total Sales" value={inr(sales?.total_sales || 0)} />
        <Kpi label="Purchase Cost (PO)" value={inr(sales?.total_purchase || 0)} />
        <Kpi label="Gross Margin" value={inr(sales?.gross_margin || 0)} sub={sales?.margin_pct ? `${sales.margin_pct}% margin on purchase · ${sales.orders_with_po} order(s)` : ""} accent={(sales?.gross_margin || 0) >= 0 ? "text-emerald-700" : "text-rose-700"} />
        <Kpi label="Order Count" value={sales?.order_count || 0} />
        <Kpi label="Outstanding Invoices" value={outstanding.length} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-slate-200 rounded-md bg-white p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Top Customers</div>
          {sales?.by_customer?.length ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height={256} minWidth={0}>
                <BarChart data={sales.by_customer.slice(0, 8)} layout="vertical" margin={{ left: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis dataKey="name" type="category" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} width={120} />
                  <Tooltip formatter={(v) => inr(v)} contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
                  <Bar dataKey="amount" fill="#3B82F6" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <div className="text-sm text-slate-500 py-8 text-center">No sales data yet.</div>}
        </div>
        <div className="border border-slate-200 rounded-md bg-white p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Top Products</div>
          {sales?.by_product?.length ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height={256} minWidth={0}>
                <BarChart data={sales.by_product.slice(0, 8)} layout="vertical" margin={{ left: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis dataKey="name" type="category" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} width={120} />
                  <Tooltip formatter={(v) => inr(v)} contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
                  <Bar dataKey="amount" fill="#FC5F5F" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <div className="text-sm text-slate-500 py-8 text-center">No product sales yet.</div>}
        </div>
      </div>

      <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
        <div className="px-4 py-2.5 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">Outstanding Invoices</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-2">Invoice</th>
              <th className="text-left px-4 py-2">Customer</th>
              <th className="text-left px-4 py-2">Due</th>
              <th className="text-right px-4 py-2">Total</th>
              <th className="text-right px-4 py-2">Paid</th>
              <th className="text-right px-4 py-2">Balance</th>
              <th className="text-left px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {outstanding.map((i) => (
              <tr key={i.id} className="border-t border-slate-100">
                <td className="px-4 py-2 font-mono">{i.number}</td>
                <td className="px-4 py-2">{i.customer_snapshot?.company_name}</td>
                <td className="px-4 py-2">{fmtDate(i.due_date)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{inr(i.total)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{inr(i.paid)}</td>
                <td className="px-4 py-2 text-right tabular-nums text-rose-700 font-medium">{inr(i.total - i.paid)}</td>
                <td className="px-4 py-2"><StatusPill status={i.status} /></td>
              </tr>
            ))}
            {outstanding.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-sm text-slate-500">No outstanding invoices.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InsightsTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/reports/customer-insights").then((r) => setData(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, []);
  if (error) return <div className="text-sm text-rose-600 py-8" data-testid="insights-error">Could not load insights: {error}</div>;
  if (!data) return <div className="text-sm text-slate-500 py-8">Loading insights…</div>;
  return (
    <div className="space-y-4" data-testid="insights-tab">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Kpi label="Quote → Order Conversion" value={`${data.conversion.rate_pct}%`}
             sub={`${data.conversion.converted} of ${data.conversion.total_quotes} quotations converted`}
             accent="text-[#C94A4A]" />
        <Kpi label="Total Quotations" value={data.conversion.total_quotes} />
        <Kpi label="Orders Won" value={data.conversion.converted} accent="text-emerald-700" />
        <Kpi label="Repeat Customers" value={data.repeat_customers} sub="customers with 2+ orders" />
      </div>

      <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
        <div className="px-4 py-2.5 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-2">
          <TrendingUp size={13} className="text-[#FC5F5F]" /> Customer Intelligence
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="insights-table">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Customer</th>
                <th className="text-right px-4 py-2">Orders</th>
                <th className="text-right px-4 py-2">Revenue</th>
                <th className="text-left px-4 py-2">Last Order</th>
                <th className="text-right px-4 py-2">Avg Gap</th>
                <th className="text-right px-4 py-2">Quotes → Orders</th>
                <th className="text-left px-4 py-2">Top Products</th>
              </tr>
            </thead>
            <tbody>
              {data.customers.map((c, i) => (
                <tr key={i} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors duration-200">
                  <td className="px-4 py-2.5 font-medium text-slate-800">
                    {c.name}
                    {c.repeat && <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5"><Repeat size={9} /> Repeat</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{c.orders}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-medium">{inr(c.revenue)}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{c.last_order ? fmtDate(c.last_order) : "—"}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-xs">{c.avg_gap_days != null ? `${c.avg_gap_days} d` : "—"}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {c.quotes ? <span>{c.converted}/{c.quotes} <b className={c.conv_rate >= 50 ? "text-emerald-700" : "text-slate-600"}>({c.conv_rate}%)</b></span> : "—"}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {c.top_products.map((p, j) => (
                        <span key={j} className="text-[10px] bg-slate-100 border border-slate-200 text-slate-600 rounded px-1.5 py-0.5" title={inr(p.amount)}>{p.name}</span>
                      ))}
                      {!c.top_products.length && <span className="text-xs text-slate-400">—</span>}
                    </div>
                  </td>
                </tr>
              ))}
              {data.customers.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-sm text-slate-500">No customer data yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function GstTab() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const load = (m) => api.get("/reports/gst", { params: { month: m } })
    .then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(month); }, [month]);
  return (
    <div className="space-y-4" data-testid="gst-tab">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={month.slice(5, 7)} onValueChange={(m) => setMonth(`${month.slice(0, 4)}-${m}`)}>
          <SelectTrigger className="w-36" data-testid="gst-month-select"><SelectValue placeholder="Month" /></SelectTrigger>
          <SelectContent>
            {["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"].map((m) => (
              <SelectItem key={m} value={m}>{new Date(`2000-${m}-01`).toLocaleString("en", { month: "long" })}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={month.slice(0, 4)} onValueChange={(y) => setMonth(`${y}-${month.slice(5, 7)}`)}>
          <SelectTrigger className="w-28" data-testid="gst-year-select"><SelectValue placeholder="Year" /></SelectTrigger>
          <SelectContent>
            {Array.from({ length: 6 }, (_, i) => String(new Date().getFullYear() - i)).map((y) => (
              <SelectItem key={y} value={y}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={() => window.open(`${API_URL}/reports/gst/export?month=${month}`, "_blank")}
                className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="gst-export"
                disabled={!data?.rows?.length}>
          <Download size={14} className="mr-1.5" /> Export Excel
        </Button>
        {data && <span className="text-xs text-slate-500">{data.rows.length} invoice(s) in {month}</span>}
      </div>

      <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
        <div className="px-4 py-2.5 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">GST Sales Register — {month}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="gst-table">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Invoice</th>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">GSTIN</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-right px-3 py-2">Taxable</th>
                <th className="text-right px-3 py-2">CGST</th>
                <th className="text-right px-3 py-2">SGST</th>
                <th className="text-right px-3 py-2">IGST</th>
                <th className="text-right px-3 py-2">Freight</th>
                <th className="text-right px-3 py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-xs">{r.number}</td>
                  <td className="px-3 py-2 text-xs">{fmtDate(r.date)}</td>
                  <td className="px-3 py-2">{r.customer}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.gstin || "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.sale_type}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{num(r.taxable)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{num(r.cgst)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{num(r.sgst)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{num(r.igst)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{num(r.freight)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{num(r.total)}</td>
                </tr>
              ))}
              {data?.rows?.length > 0 && (
                <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold" data-testid="gst-totals">
                  <td className="px-3 py-2" colSpan={5}>TOTAL</td>
                  <td className="px-3 py-2 text-right tabular-nums">{num(data.totals.taxable)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{num(data.totals.cgst)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{num(data.totals.sgst)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{num(data.totals.igst)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{num(data.totals.freight)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{num(data.totals.total)}</td>
                </tr>
              )}
              {(!data || data.rows.length === 0) && <tr><td colSpan={11} className="text-center py-10 text-sm text-slate-500">No invoices in {month}.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/reports/analytics").then((r) => setData(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, []);
  if (error) return <div className="text-sm text-rose-600 py-8" data-testid="analytics-error">Could not load analytics: {error}</div>;
  if (!data) return <div className="text-sm text-slate-500 py-8">Loading analytics…</div>;
  const fmtMonth = (m) => new Date(`${m}-01`).toLocaleString("en", { month: "short", year: "2-digit" });
  const AGING_COLORS = { "Not yet due": "#94A3B8", "1-30 days": "#F59E0B", "31-60 days": "#F97316", "61-90 days": "#EF4444", "90+ days": "#991B1B" };
  const STATUS_COLORS = { Paid: "#10B981", Partial: "#F59E0B", Unpaid: "#94A3B8", Overdue: "#EF4444" };
  return (
    <div className="space-y-4" data-testid="analytics-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Sales (12 mo)" value={inr(data.kpis.sales_12m)} />
        <Kpi label="Collections (12 mo)" value={inr(data.kpis.collections_12m)} accent="text-emerald-700" />
        <Kpi label="Total Outstanding" value={inr(data.kpis.outstanding_total)} accent="text-amber-700" />
        <Kpi label="Overdue Amount" value={inr(data.kpis.overdue_total)} accent="text-rose-700" />
      </div>

      <div className="border border-slate-200 rounded-md bg-white p-5">
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Sales vs Collections — last 12 months</div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height={288} minWidth={0}>
            <AreaChart data={data.monthly} margin={{ left: 10, right: 10 }}>
              <defs>
                <linearGradient id="gSales" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="gColl" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="month" tickFormatter={fmtMonth} stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
              <Tooltip formatter={(v) => inr(v)} labelFormatter={fmtMonth} contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="sales" name="Sales (invoiced)" stroke="#3B82F6" strokeWidth={2} fill="url(#gSales)" />
              <Area type="monotone" dataKey="collections" name="Collections (received)" stroke="#10B981" strokeWidth={2} fill="url(#gColl)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-slate-200 rounded-md bg-white p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Payment Aging — outstanding balance</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height={256} minWidth={0}>
              <BarChart data={data.aging} margin={{ left: 10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="bucket" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <Tooltip formatter={(v, n, p) => [`${inr(v)} · ${p.payload.count} invoice(s)`, "Balance"]} contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
                <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                  {data.aging.map((a, i) => <Cell key={i} fill={AGING_COLORS[a.bucket] || "#94A3B8"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="border border-slate-200 rounded-md bg-white p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Invoice Value by Status</div>
          {data.status_split.length ? (
            <div className="h-64 flex items-center">
              <ResponsiveContainer width="100%" height={256} minWidth={0}>
                <PieChart>
                  <Pie data={data.status_split} dataKey="value" nameKey="name" innerRadius={62} outerRadius={95} paddingAngle={3}>
                    {data.status_split.map((s, i) => <Cell key={i} fill={STATUS_COLORS[s.name] || "#64748B"} />)}
                  </Pie>
                  <Tooltip formatter={(v) => inr(v)} contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : <div className="text-sm text-slate-500 py-8 text-center">No invoices yet.</div>}
        </div>
      </div>
    </div>
  );
}

export default function Reports() {
  return (
    <div className="space-y-4" data-testid="reports-page">
      <PageHeader title="Reports" subtitle="Business intelligence, customer insights & GST" />
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview" data-testid="rep-overview-tab">Overview</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="rep-analytics-tab">Analytics</TabsTrigger>
          <TabsTrigger value="insights" data-testid="rep-insights-tab">Customer Insights</TabsTrigger>
          <TabsTrigger value="trends" data-testid="rep-trends-tab">Buying Trends</TabsTrigger>
          <TabsTrigger value="gst" data-testid="rep-gst-tab">GST Register</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4"><OverviewTab /></TabsContent>
        <TabsContent value="analytics" className="mt-4"><AnalyticsTab /></TabsContent>
        <TabsContent value="insights" className="mt-4"><InsightsTab /></TabsContent>
        <TabsContent value="trends" className="mt-4"><BuyingTrendsTab /></TabsContent>
        <TabsContent value="gst" className="mt-4"><GstTab /></TabsContent>
      </Tabs>
    </div>
  );
}
